import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import { ESignInfoSection } from "eSign/components/DocumentDetails/EsignInfoSection";
import ReadonlyProductsPanelSegment from "Products/ReadonlyProductsPanelSegment";
import React from "react";
import DocumentChecklistPanel from "../../checklist/DocumentChecklistPanel";
import { Modal } from "../../components";
import ReadonlyLinkedShowsPanelSegment from "../../document/linkshow/ReadonlyLinkedShowsPanelSegment";
import TwoColumnLayout from "../../layout/TwoColumnLayout";
import ESignActivityPanel from "../components/activity/ESignActivityPanel";
import ChangeLifecycleButtons from "../components/ChangeLifecycleButtons";
import SendOutDocument from "../components/modals/SendOutDocument";
import ESignOptionsPanel from "../components/optionspanel/ESignOptionsPanel";
import ViewDocumentButton from "../components/ViewDocumentButton";
import FilesPanel from "../panelsegment/FilesPanel";
import ReadonlyFilesPanelSegment from "../panelsegment/ReadonlyFilesPanelSegment";
import ViewerPanelSegment from "../panelsegment/ViewerPanelSegment";
import { SenderPanel } from "eSign/panelsegment/SenderPanel";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { SignerPanel } from "eSign/panelsegment/SignerPanel";
import NotesPanel from "note/list/app";
import { DocumentReminderApp } from "DocumentReminder/AppForDocuments";

interface Props {
  viewModel: server.viewmodel.ESignDetailFrontModel
}

const ReviewPanel = (props: Props) => {
  const { viewModel } = props;
  const [displayConfirmation, setDisplayConfirmation] = React.useState(false);
  const [otherFiles, setOtherFiles] = React.useState<server.dto.ESignFile[]>(viewModel.otherFiles);
  const [checklistItems, setChecklistItems] = React.useState<server.dto.DocumentChecklistItem[]>(viewModel.checklistItems);
  const [hasTriedSubmitting, setHasTriedSubmitting] = React.useState(false);

  const isChecklistFinished = () => {
    if (checklistItems.length === 0) return true;

    return checklistItems.every(x => x.state === "approved");
  }

  const canProgressToNextStep = () => {
    const allChecked = isChecklistFinished();

    if (!allChecked) {
      window.soneTools.scrollTo("#reviewChecklist");
    }

    setHasTriedSubmitting(true);

    return allChecked;
  }

  const documentType = viewModel.details.documentType as Exclude<server.dto.DocumentTypes, "unknown">;

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <DetailSectionTemplate
        isInPageEditable={false}
        onValueChanged={() => { }}
        sections={[
          {
            type: "custom",
            key: "infoSection",
            render: () => <ESignInfoSection
              documentType={documentType}
              esignDocument={viewModel.details}
              stepOptions={{
                draft: { disabled: false },
                review: { disabled: false },
                pending: { disabled: !isChecklistFinished(), validate: canProgressToNextStep, onClick: () => { setDisplayConfirmation(true); } },
                completed: { disabled: true }
              }}
              activeStep="review"
              overDue={false}
              brands={viewModel.brands}
            />
          },
          SenderPanel({
            esignDocument: viewModel.details,
            sender: viewModel.sender,
            progress: viewModel.senderProgress,
          }),
          SignerPanel({
            esignDocument: viewModel.details,
            signer: viewModel.signer,
            progress: viewModel.signerProgress
          }),
          {
            type: "custom",
            key: "viewDocumentButton",
            render: () => <ViewDocumentButton
              documentNumber={viewModel.details.documentNumber}
              label={RESX.ESign.ViewDocument}
              version={viewModel.details.version}
              documentId={viewModel.details.documentId}
            />
          },
          {
            type: "custom",
            key: "viewPanel",
            render: () => <ViewerPanelSegment
              initialViewers={viewModel.viewers}
              document={viewModel.details}
            />
          }, {
            type: "custom",
            key: "checklistPanel",
            render: () => <DocumentChecklistPanel
              id="reviewChecklist"
              checklistItems={checklistItems}
              hasTriedSubmitting={hasTriedSubmitting}
              documentId={viewModel.details.documentId}
              onChecklistItemsChanged={(items) => { setChecklistItems(items); }}
              setApi={window.CoreApi.Checklist.SetESignChecklistItemState}
              whoCanReviewApi={window.CoreApi.Checklist.WhoCanReviewESignChecklistItem}
              listApi={window.CoreApi.Checklist.ListESignChecklistItems}
            />
          }, {
            type: "custom",
            key: "productsPanel",
            render: () => <ReadonlyProductsPanelSegment
              productsPanel={viewModel.productsPanel}
            />
          }, {
            type: "custom",
            key: "linkedShowsPanel",
            render: () => <ReadonlyLinkedShowsPanelSegment
              documentType={viewModel.details.documentType}
              linkedShowsPanel={viewModel.linkedShowsPanel}
            />
          }, {
            type: "custom",
            key: "filesThatNeedSigningPanel",
            render: () => <ReadonlyFilesPanelSegment
              label={RESX.ESign.PdfsThatNeedToBeSigned}
              files={viewModel.filesThatNeedToBeSigned}
            />
          }, {
            key: "otherFilesPanel",
            type: "custom",
            render: () => <FilesPanel
              label={RESX.ESign.OtherFiles}
              fileGroup="otherFiles"
              files={otherFiles}
              onFilesUpdated={files => { setOtherFiles(files.filter(f => f.fileGroup === "otherFiles")); }}
              error={false}
              documentDetails={viewModel.details}
              startUncollapsed
              collapsible={true}
              articleId={serverReference.supportArticleIds.eSignOtherFiles}
              bookkeeping={viewModel.details.bookkeeping}
            />
          }, {
            key: "notesPanel",
            type: "custom",
            render: () => <NotesPanel
              mainArea={documentType}
              useSectionPanel
              config={viewModel.linkedNotePanelConfig}
            />
          }, {
            type: "custom",
            key: "changeLifecycleButtons",
            render: () => <SectionPanel.Panel>
              <ChangeLifecycleButtons
                documentId={viewModel.details.documentId}
                left="draft"
                right="pending"
                rightOnClick={() => { setDisplayConfirmation(true); }}
                rightValidate={canProgressToNextStep}
                documentType={documentType}
              />
            </SectionPanel.Panel>
          }
        ]}
      />

      <Modal
        isOpen={displayConfirmation}
        handleClose={() => { setDisplayConfirmation(false); }}
        width="tiny"
        title={RESX.ESign.SendInvitations}
      >
        <SendOutDocument
          documentId={viewModel.details.documentId}
          files={viewModel.filesThatNeedToBeSigned}
          documentType={documentType}
        />
      </Modal>
    </TwoColumnLayout.LeftColumn>
    <TwoColumnLayout.RightColumn size="medium">
      <ESignOptionsPanel
        optionsPanel={viewModel.optionsPanel}
        listUrl={viewModel.listUrl}
        documentDetails={props.viewModel.details}
        copyUrl={viewModel.copyUrl}
      />

      <DocumentReminderApp
        document={viewModel.details}
        initialReminders={viewModel.reminders}
      />
      <ESignActivityPanel documentId={viewModel.details.documentId} config={viewModel.activityPanel} />
    </TwoColumnLayout.RightColumn>
  </TwoColumnLayout>
}

export default ReviewPanel;